<template>
  <!-- Tutorial Onboarding - Cores Originais + Auto Comentário -->
  <Teleport to="body">
    <Transition name="tutorial-fade">
      <div v-if="tutorialStore.isActive" class="tutorial-overlay">
        <!-- Backdrop com recorte para mostrar cores originais -->
        <div
          v-if="spotlightStyle"
          class="tutorial-backdrop-mask"
          :style="backdropMaskStyle"
        ></div>

        <!-- Spotlight no elemento atual - apenas borda visual -->
        <div
          v-if="spotlightStyle"
          class="tutorial-spotlight"
          :style="spotlightStyle"
        ></div>

        <!-- Card de instrução -->
        <div class="tutorial-card" :style="cardStyle">
          <!-- Progress bar -->
          <div class="tutorial-progress-bar">
            <div
              class="tutorial-progress-fill"
              :style="{ width: tutorialStore.progress + '%' }"
            ></div>
          </div>

          <!-- Header -->
          <div class="tutorial-header">
            <span class="tutorial-step-indicator">
              Passo {{ tutorialStore.currentStep + 1 }} de
              {{ tutorialStore.totalSteps }}
            </span>
            <button
              @click="handleSkip"
              class="tutorial-close-btn"
              aria-label="Fechar tutorial"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>

          <!-- Content -->
          <div class="tutorial-content">
            <h3 class="tutorial-title">
              {{ tutorialStore.currentStepData.title }}
            </h3>
            <p class="tutorial-description">
              {{ tutorialStore.currentStepData.description }}
            </p>

            <!-- Indicador de interação obrigatória -->
            <div
              v-if="needsInteraction && !tutorialStore.interactionCompleted"
              class="tutorial-hint"
            >
              <svg
                class="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122"
                ></path>
              </svg>
              <span>Clique no botão destacado para continuar</span>
            </div>
          </div>

          <!-- Actions -->
          <div class="tutorial-actions">
            <button
              v-if="!tutorialStore.isFirstStep"
              @click="tutorialStore.previousStep"
              class="tutorial-btn tutorial-btn-secondary"
            >
              ← Anterior
            </button>

            <div class="tutorial-actions-right">
              <button
                @click="handleSkip"
                class="tutorial-btn tutorial-btn-text"
              >
                Pular Tutorial
              </button>

              <button
                @click="handleNext"
                class="tutorial-btn tutorial-btn-primary"
                :disabled="isNextDisabled"
                :class="{
                  'tutorial-btn-disabled': isNextDisabled,
                  'tutorial-btn-pulse':
                    needsInteraction && !tutorialStore.interactionCompleted,
                }"
              >
                {{ tutorialStore.isLastStep ? "Concluir ✓" : "Próximo →" }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from "vue";
import { useTutorialStore } from "@/store/tutorial";

const tutorialStore = useTutorialStore();

console.log("🎯 TUTORIAL COMPONENT LOADED - Versão com backdrop SEM BLUR");
console.log("📝 Cores originais dos elementos devem estar visíveis");

// Refs para elementos do DOM
const spotlightStyle = ref(null);
const cardStyle = ref(null);
const backdropMaskStyle = ref(null);

// Computed para saber se step atual precisa de interação
const needsInteraction = computed(() => {
  return tutorialStore.currentStepData?.requiresInteraction || false;
});

const isNextDisabled = computed(() => {
  return needsInteraction.value && !tutorialStore.interactionCompleted;
});

// Calcular posição do spotlight e card
const calculatePositions = () => {
  const targetSelector = tutorialStore.currentStepData?.target;
  if (!targetSelector) return;

  // Aguardar próximo tick para garantir que DOM foi atualizado
  nextTick(() => {
    const targetElement = document.querySelector(targetSelector);

    if (!targetElement) {
      console.warn(
        `⚠️ Elemento não encontrado: ${targetSelector}. Ajustando posição padrão.`
      );
      // Posição padrão se elemento não existir
      spotlightStyle.value = null;
      cardStyle.value = {
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
      };
      return;
    }

    // Scroll suave para o elemento
    targetElement.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "center",
    });

    // Aguardar scroll completar antes de calcular posições
    setTimeout(() => {
      const rect = targetElement.getBoundingClientRect();
      const padding = 12; // Espaço ao redor do elemento

      // Estilo do spotlight (caixa destacada)
      spotlightStyle.value = {
        top: `${rect.top - padding}px`,
        left: `${rect.left - padding}px`,
        width: `${rect.width + padding * 2}px`,
        height: `${rect.height + padding * 2}px`,
      };

      // Criar backdrop com recorte (buraco) onde está o elemento para mostrar cores originais
      backdropMaskStyle.value = {
        clipPath: `polygon(
        0% 0%, 
        0% 100%, 
        ${rect.left - padding}px 100%, 
        ${rect.left - padding}px ${rect.top - padding}px, 
        ${rect.right + padding}px ${rect.top - padding}px, 
        ${rect.right + padding}px ${rect.bottom + padding}px, 
        ${rect.left - padding}px ${rect.bottom + padding}px, 
        ${rect.left - padding}px 100%, 
        100% 100%, 
        100% 0%
      )`,
      };

      // Posição do card baseado na posição configurada
      const position = tutorialStore.currentStepData.position || "bottom";
      const cardWidth = 420;
      const cardHeight = 280; // Altura aproximada (aumentada para incluir hint)
      const cardOffset = 40; // Distância do elemento (aumentada de 24 para 40)

      let top, left, transform;

      if (position === "bottom") {
        top = rect.bottom + cardOffset;
        left = rect.left + rect.width / 2;
        transform = "translateX(-50%)";

        // Se não couber embaixo, colocar em cima
        if (top + cardHeight > window.innerHeight - 20) {
          top = rect.top - cardOffset - cardHeight;
        }
      } else if (position === "top") {
        top = rect.top - cardOffset - cardHeight;
        left = rect.left + rect.width / 2;
        transform = "translateX(-50%)";

        // Se não couber em cima, colocar à direita ou esquerda
        if (top < 20) {
          // Tentar à direita primeiro
          if (rect.right + cardOffset + cardWidth < window.innerWidth - 20) {
            top = rect.top + rect.height / 2;
            left = rect.right + cardOffset;
            transform = "translateY(-50%)";
          } else if (rect.left - cardOffset - cardWidth > 20) {
            // Se não couber à direita, tentar à esquerda
            top = rect.top + rect.height / 2;
            left = rect.left - cardOffset - cardWidth;
            transform = "translateY(-50%)";
          } else {
            // Última opção: embaixo com mais espaço
            top = rect.bottom + cardOffset + 20;
            left = rect.left + rect.width / 2;
            transform = "translateX(-50%)";
          }
        }
      } else if (position === "left") {
        top = rect.top + rect.height / 2;
        left = rect.left - cardOffset - cardWidth;
        transform = "translateY(-50%)";

        // Se não couber à esquerda, colocar à direita
        if (left < 20) {
          left = rect.right + cardOffset;
        }
      } else if (position === "right") {
        top = rect.top + rect.height / 2;
        left = rect.right + cardOffset;
        transform = "translateY(-50%)";

        // Se não couber à direita, colocar à esquerda
        if (left + cardWidth > window.innerWidth - 20) {
          left = rect.left - cardOffset - cardWidth;
        }
      }

      // Garantir que card não saia da tela (ajuste fino responsivo)
      const margin = window.innerWidth < 640 ? 16 : 20;
      const maxWidth =
        window.innerWidth < 640 ? window.innerWidth - 32 : cardWidth;

      top = Math.max(
        margin,
        Math.min(top, window.innerHeight - cardHeight - margin)
      );
      left = Math.max(
        margin,
        Math.min(left, window.innerWidth - maxWidth - margin)
      );

      // Verificar colisão com o elemento target (garantir que card não cubra o botão)
      const cardRect = {
        top: top,
        left: left,
        right: left + cardWidth,
        bottom: top + cardHeight,
      };

      const hasCollision = !(
        cardRect.right < rect.left ||
        cardRect.left > rect.right ||
        cardRect.bottom < rect.top ||
        cardRect.top > rect.bottom
      );

      // Se houver colisão, ajustar posição para baixo do elemento
      if (hasCollision) {
        console.log("⚠️ Colisão detectada! Reposicionando card...");
        top = rect.bottom + cardOffset + 30;
        left = window.innerWidth / 2;
        transform = "translateX(-50%)";

        // Garantir que ainda está na tela
        top = Math.min(top, window.innerHeight - cardHeight - margin);
      }

      cardStyle.value = {
        top: `${top}px`,
        left: `${left}px`,
        transform: transform,
      };
    }, 300); // Tempo para scroll suave completar
  });
};

// Handlers
const handleNext = async () => {
  if (tutorialStore.isLastStep) {
    await tutorialStore.completeTutorial();
  } else {
    tutorialStore.nextStep();
  }
};

const handleSkip = async () => {
  if (
    confirm(
      "Tem certeza que deseja pular o tutorial? Você pode revê-lo depois nas configurações."
    )
  ) {
    await tutorialStore.skipTutorial();
  }
};

// Watch para recalcular posições quando step mudar
watch(
  () => tutorialStore.currentStep,
  () => {
    calculatePositions();
    setupInteractionListeners();
  }
);

// Watch para recalcular quando tutorial ativar
watch(
  () => tutorialStore.isActive,
  (isActive) => {
    if (isActive) {
      calculatePositions();
      setupInteractionListeners();
    } else {
      removeInteractionListeners();
    }
  }
);

// Setup de listeners para interação
const setupInteractionListeners = () => {
  removeInteractionListeners(); // Remove listeners antigos

  const currentStep = tutorialStore.currentStepData;
  if (!currentStep?.requiresInteraction) return;

  nextTick(() => {
    const targetElement = document.querySelector(currentStep.target);
    if (!targetElement) {
      console.warn(`⚠️ Elemento target não encontrado: ${currentStep.target}`);
      return;
    }

    console.log("🎯 Configurando interação para:", currentStep.target);

    // Encontrar o post-card pai se for botão de like/comment
    const postCard = targetElement.closest(".post-card");

    // Habilitar pointer-events em TODOS os elementos do caminho
    let element = targetElement;
    const elementsToRestore = [];
    while (element && element !== document.body) {
      if (element.style.pointerEvents !== "auto") {
        elementsToRestore.push({
          element: element,
          originalValue: element.style.pointerEvents || "auto",
        });
        element.style.pointerEvents = "auto";
      }
      element = element.parentElement;
    }

    // Salvar lista para restauração
    targetElement.dataset.elementsToRestore = JSON.stringify(
      elementsToRestore.map((item) => ({
        selector: item.element.className,
        originalValue: item.originalValue,
      }))
    );

    // Permitir cliques no target
    targetElement.style.cursor = "pointer";

    // Elevar z-index do post inteiro para ficar acima do backdrop
    if (postCard) {
      postCard.dataset.originalZIndex = postCard.style.zIndex || "auto";
      postCard.style.zIndex = "1000000";
      postCard.style.position = "relative";
      postCard.style.pointerEvents = "auto";
      console.log("✅ Z-index do post-card elevado");
    }

    // Elevar z-index do elemento target também
    targetElement.dataset.originalZIndex = targetElement.style.zIndex || "auto";
    targetElement.style.zIndex = "1000001";
    targetElement.style.position = "relative";

    // Card do tutorial também precisa ter pointer-events
    const tutorialCard = document.querySelector(".tutorial-card");
    if (tutorialCard) tutorialCard.style.pointerEvents = "auto";

    if (currentStep.interactionType === "like") {
      targetElement.addEventListener("click", handleLikeInteraction, {
        capture: true,
        once: true,
      });
      targetElement.style.cursor = "pointer";
    } else if (currentStep.interactionType === "comment") {
      targetElement.addEventListener("click", handleCommentInteraction, {
        capture: true,
        once: true,
      });
      targetElement.style.cursor = "pointer";
    }
  });
};

const removeInteractionListeners = () => {
  const currentStep = tutorialStore.currentStepData;
  if (!currentStep?.target) return;

  const targetElement = document.querySelector(currentStep.target);
  if (targetElement) {
    targetElement.removeEventListener("click", handleLikeInteraction, {
      capture: true,
    });
    targetElement.removeEventListener("click", handleCommentInteraction, {
      capture: true,
    });
    targetElement.style.cursor = "";
    targetElement.style.pointerEvents = "";

    // Restaurar z-index do elemento
    if (targetElement.dataset.originalZIndex) {
      targetElement.style.zIndex = targetElement.dataset.originalZIndex;
      delete targetElement.dataset.originalZIndex;
    }

    // Restaurar z-index do post-card pai
    const postCard = targetElement.closest(".post-card");
    if (postCard && postCard.dataset.originalZIndex) {
      postCard.style.zIndex = postCard.dataset.originalZIndex;
      delete postCard.dataset.originalZIndex;
    }
  }
};

const handleLikeInteraction = (event) => {
  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();

  console.log("❤️ Like clicado no tutorial (simulado)");

  // Adicionar efeito visual temporário
  const button = event.currentTarget;
  const icon = button.querySelector(".action-icon");
  if (icon) {
    icon.classList.add("action-icon-filled");
    button.classList.add("action-active");
  }

  tutorialStore.completeInteraction();
};

const handleCommentInteraction = async () => {
  console.log("💬 [TUTORIAL] Step de comentário iniciado");

  // Ativar flag para auto-scroll quando página carregar
  tutorialStore.enableCommentAutoScroll();

  // NÃO bloquear o clique - deixar ele abrir o post naturalmente
  // O auto-scroll será feito pelo router afterEach quando a página carregar
};

// Recalcular em resize
const handleResize = () => {
  if (tutorialStore.isActive) {
    calculatePositions();
  }
};

// Listener para forçar recálculo (usado quando muda de página no tutorial)
const handleTutorialUpdate = () => {
  console.log("🔄 Evento tutorial-update-positions recebido");
  if (tutorialStore.isActive) {
    calculatePositions();
    setupInteractionListeners();
  }
};

onMounted(() => {
  window.addEventListener("resize", handleResize);
  window.addEventListener("tutorial-update-positions", handleTutorialUpdate);
  if (tutorialStore.isActive) {
    calculatePositions();
    setupInteractionListeners();
  }
});

onUnmounted(() => {
  window.removeEventListener("resize", handleResize);
  window.removeEventListener("tutorial-update-positions", handleTutorialUpdate);
  removeInteractionListeners();
});
</script>

<style scoped>
/* Overlay principal */
.tutorial-overlay {
  position: fixed;
  inset: 0;
  z-index: 999999;
  pointer-events: none;
}

/* Backdrop com recorte para mostrar cores originais do elemento */
.tutorial-backdrop-mask {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  pointer-events: none;
  z-index: 999996;
  transition: clip-path 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Spotlight - apenas borda visual, não bloqueia cliques */
.tutorial-spotlight {
  position: fixed;
  border-radius: 12px;
  border: 4px solid rgba(59, 130, 246, 1);
  box-shadow: 0 0 30px rgba(59, 130, 246, 0.8), 0 0 60px rgba(59, 130, 246, 0.4);
  pointer-events: none;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 999997;
  background: transparent;
  animation: pulse-spotlight 2s ease-in-out infinite;
}

@keyframes pulse-spotlight {
  0%,
  100% {
    border-color: rgba(59, 130, 246, 1);
    box-shadow: 0 0 30px rgba(59, 130, 246, 0.8),
      0 0 60px rgba(59, 130, 246, 0.4);
  }
  50% {
    border-color: rgba(96, 165, 250, 1);
    box-shadow: 0 0 40px rgba(59, 130, 246, 1), 0 0 80px rgba(59, 130, 246, 0.6);
  }
}

/* Card de instrução */
.tutorial-card {
  position: fixed;
  width: 420px;
  max-width: calc(100vw - 40px);
  background: white;
  border-radius: 16px;
  box-shadow: 0 25px 70px rgba(0, 0, 0, 0.4), 0 10px 40px rgba(0, 0, 0, 0.3);
  pointer-events: auto;
  z-index: 1000000;
  animation: slideIn 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Progress bar */
.tutorial-progress-bar {
  height: 4px;
  background: #e5e7eb;
  border-radius: 16px 16px 0 0;
  overflow: hidden;
}

.tutorial-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #3b82f6, #8b5cf6);
  transition: width 0.4s ease;
}

/* Header */
.tutorial-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px 12px;
}

.tutorial-step-indicator {
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.tutorial-close-btn {
  padding: 4px;
  border: none;
  background: transparent;
  color: #9ca3af;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.tutorial-close-btn:hover {
  background: #f3f4f6;
  color: #374151;
}

/* Content */
.tutorial-content {
  padding: 0 20px 20px;
}

.tutorial-title {
  font-size: 20px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 12px 0;
}

.tutorial-description {
  font-size: 15px;
  line-height: 1.6;
  color: #4b5563;
  margin: 0;
}

.tutorial-hint {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
  padding: 12px;
  background: linear-gradient(
    135deg,
    rgba(59, 130, 246, 0.1),
    rgba(139, 92, 246, 0.1)
  );
  border: 1px solid rgba(59, 130, 246, 0.3);
  border-radius: 8px;
  color: #3b82f6;
  font-size: 14px;
  font-weight: 600;
  animation: hint-pulse 2s ease-in-out infinite;
}

.tutorial-hint svg {
  flex-shrink: 0;
}

@keyframes hint-pulse {
  0%,
  100% {
    transform: translateX(0);
  }
  50% {
    transform: translateX(4px);
  }
}

/* Actions */
.tutorial-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-top: 1px solid #e5e7eb;
}

.tutorial-actions-right {
  display: flex;
  gap: 12px;
  margin-left: auto;
}

.tutorial-btn {
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  white-space: nowrap;
}

.tutorial-btn-primary {
  background: linear-gradient(135deg, #3b82f6, #8b5cf6);
  color: white;
}

.tutorial-btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
}

.tutorial-btn-primary:disabled,
.tutorial-btn-disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none !important;
  box-shadow: none !important;
}

.tutorial-btn-pulse {
  animation: pulse-button 2s ease-in-out infinite;
}

@keyframes pulse-button {
  0%,
  100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
}

.tutorial-btn-secondary {
  background: #f3f4f6;
  color: #374151;
}

.tutorial-btn-secondary:hover {
  background: #e5e7eb;
}

.tutorial-btn-text {
  background: transparent;
  color: #6b7280;
  padding: 10px 16px;
}

.tutorial-btn-text:hover {
  color: #374151;
  background: #f9fafb;
}

/* Animations */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Transition classes */
.tutorial-fade-enter-active,
.tutorial-fade-leave-active {
  transition: opacity 0.3s ease;
}

.tutorial-fade-enter-from,
.tutorial-fade-leave-to {
  opacity: 0;
}

/* Responsivo */
@media (max-width: 640px) {
  .tutorial-card {
    width: calc(100vw - 32px);
    max-width: none;
    position: fixed !important;
    bottom: 16px !important;
    left: 16px !important;
    right: 16px !important;
    top: auto !important;
    transform: none !important;
  }

  .tutorial-spotlight {
    border-radius: 8px;
  }

  .tutorial-actions {
    flex-direction: column;
    gap: 8px;
  }

  .tutorial-actions-right {
    width: 100%;
    flex-direction: column;
    margin-left: 0;
  }

  .tutorial-btn {
    width: 100%;
  }

  .tutorial-title {
    font-size: 18px;
  }

  .tutorial-description {
    font-size: 14px;
  }
}

/* Tablet */
@media (min-width: 641px) and (max-width: 1024px) {
  .tutorial-card {
    width: 380px;
  }
}
</style>
